import AnimatedSvg, { anim, DELAY, FILL_BOX, resolveTrigger } from "./AnimatedSvg";

/**
 * Uyarı ikon seti (#229) — her uyarı tipinin hareketi anlamını anlatır:
 * zil sallanır, sinyal söner, buhar kesilir, anahtar döner, kamyon yol alır,
 * kota boşalır, saat işler, grafik aşağı çizilir.
 *
 * Ortak props: { className, trigger = "always" | "hover" | "off", animated, title }
 */

/** Zil — Uyarılar menüsü / sayfa başlığı. `active` iken çevresinde dalga. */
export function AlertBellIcon({ className, animated = true, active = false, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      {active && (
        <circle
          cx="18"
          cy="6"
          r="3"
          className={`fill-current stroke-none opacity-40 origin-center ${anim(t, "iconPing")}`}
          style={FILL_BOX}
        />
      )}
      <g
        className={`origin-top ${anim(t, "iconSwing")}`}
        style={FILL_BOX}
      >
        <path d="M6 16.5V11a6 6 0 1 1 12 0v5.5l1.5 2h-15l1.5-2Z" />
        <path d="M10 20.5a2 2 0 0 0 4 0" />
      </g>
      {active && <circle cx="18" cy="6" r="2.2" className="fill-current stroke-none" />}
    </AnimatedSvg>
  );
}

/** Kritik seviyeli cihaz — ekranlı cihaz, ünlem yanıp söner, dalga yayılır. */
export function CriticalDeviceIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <circle
        cx="12"
        cy="10"
        r="7"
        className={`opacity-30 origin-center ${anim(t, "iconPing")}`}
        style={FILL_BOX}
      />
      <rect x="5" y="4" width="14" height="12" rx="2.5" />
      <path d="M9 20h6M12 16v4" />
      <g className={anim(t, "iconBlink")}>
        <path d="M12 7.5v3.5" strokeWidth={2.2} />
        <circle cx="12" cy="13.2" r="0.6" className="fill-current" />
      </g>
    </AnimatedSvg>
  );
}

/** Bağlanmayan cihaz — sinyal halkaları sırayla söner. */
export function SignalLostIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <circle cx="12" cy="18" r="1.2" className="fill-current" />
      <path
        d="M8.5 14.5a5 5 0 0 1 7 0"
        className={anim(t, "iconBlink")}
      />
      <path
        d="M5.5 11.5a9.2 9.2 0 0 1 13 0"
        className={anim(t, "iconBlink", DELAY.d1)}
      />
      <path
        d="M2.5 8.5a13.4 13.4 0 0 1 19 0"
        className={anim(t, "iconBlink", DELAY.d2)}
      />
      <path d="M4 3l16 18" strokeWidth={2} />
    </AnimatedSvg>
  );
}

/** Kota tüketmeyen cihaz — fincan; buhar yükselip kayboluyor (kullanım yok). */
export function IdleCupIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <path
        d="M9 3.5c-.8 1 .8 1.5 0 2.5"
        className={anim(t, "iconRise")}
      />
      <path
        d="M13 3.5c-.8 1 .8 1.5 0 2.5"
        className={anim(t, "iconRise", DELAY.d3)}
      />
      <path d="M5 9h12v5a5 5 0 0 1-5 5h-2a5 5 0 0 1-5-5V9Z" />
      <path d="M17 10.5h1.5a2.5 2.5 0 0 1 0 5H17" />
      <path d="M4 21h14" />
    </AnimatedSvg>
  );
}

/** Uzun süre serviste — İngiliz anahtarı ileri geri çevrilir. */
export function ServiceWrenchIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <g
        className={`origin-bottom-left ${anim(t, "iconWrench")}`}
        style={FILL_BOX}
      >
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9l-6.9 6.9a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9l-3.8 3.8Z" />
      </g>
      <circle cx="17.5" cy="17.5" r="3.5" className="opacity-40" />
      <path d="M17.5 15.8v1.9l1.2.8" />
    </AnimatedSvg>
  );
}

/** Uzun süre plasiyerde — kamyon hafifçe sekiyor, yol çizgisi akıyor. */
export function PlaymakerTruckIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <g className={anim(t, "iconBounce")}>
        <path d="M2.5 6.5h10.5v8.5H2.5z" />
        <path d="M13 9.5h4l3 3v2.5h-7" />
        <circle cx="6.5" cy="16.5" r="1.7" className="fill-white" />
        <circle cx="16.5" cy="16.5" r="1.7" className="fill-white" />
      </g>
      <path
        d="M1 20.5h22"
        strokeDasharray="3 3"
        className={`opacity-60 ${anim(t, "iconDash")}`}
      />
    </AnimatedSvg>
  );
}

/** Kritik kotalı firma — kota haznesi aşağı doğru boşalıyor. */
export function CriticalQuotaIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <rect x="6" y="4" width="12" height="17" rx="2.5" />
      <path d="M10 2.5h4" />
      <rect
        x="8.5"
        y="7"
        width="7"
        height="11.5"
        rx="1"
        className={`fill-current stroke-none opacity-35 origin-bottom ${anim(t, "iconDrain")}`}
        style={FILL_BOX}
      />
      <path d="M12 10.5v3" strokeWidth={2.2} />
      <circle cx="12" cy="16" r="0.6" className="fill-current" />
    </AnimatedSvg>
  );
}

/** Sipariş vermeyen firma — sepet; yanında saat ibresi dönüyor. */
export function NoOrderIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <path d="M2.5 4h2.2l1.9 10.2a1.5 1.5 0 0 0 1.5 1.2h7.4" />
      <path d="M5.4 7.5h8.1" />
      <circle cx="8.5" cy="19" r="1.3" />
      <circle cx="14.5" cy="19" r="1.3" />
      <circle cx="17.5" cy="8.5" r="4.5" />
      <path
        d="M17.5 8.5V6"
        className={`origin-bottom ${anim(t, "iconSpin")}`}
        style={FILL_BOX}
      />
      <path d="M17.5 8.5h1.8" />
    </AnimatedSvg>
  );
}

/**
 * Son sipariş faturası (#367) — fiş hafifçe süzülür, satırları sırayla
 * yazılır, köşedeki lira damgası vurulur. Sipariş vermeyen satırındaki
 * "fatura detayını aç" düğmesi.
 */
export function BillReceiptIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <g className={anim(t, "iconFloat")}>
        <path d="M6 3h12v17.5l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3Z" />
        <path d="M9 7.5h6" strokeDasharray="40" className={anim(t, "iconDraw")} />
        <path d="M9 11h6" strokeDasharray="40" className={anim(t, "iconDraw", DELAY.d1)} />
        <path d="M9 14.5h3.5" strokeDasharray="40" className={anim(t, "iconDraw", DELAY.d2)} />
      </g>
      <g className={`origin-center ${anim(t, "iconPop")}`} style={FILL_BOX}>
        <circle cx="18.5" cy="16.5" r="3.6" className="fill-white" />
        <path d="M17.6 14.6v3.9c1.3 0 2.3-.8 2.3-2M16.9 16.1l2-1M16.9 17.3l2-1" strokeWidth={1.4} />
      </g>
    </AnimatedSvg>
  );
}

/** Düşük performanslı firma — grafik çizgisi aşağı doğru çiziliyor. */
export function LowPerformanceIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <path d="M3.5 3.5v17h17" />
      <path
        d="M7 8l3.5 3.5 3-2.5 5.5 6.5"
        strokeDasharray="40"
        className={anim(t, "iconDraw")}
      />
      <path d="M19 12v3.5h-3.5" />
    </AnimatedSvg>
  );
}

/** Her şey yolunda — onay işareti çiziliyor. */
export function AllClearIcon({ className, animated = true, trigger, title }) {
  const t = resolveTrigger(trigger, animated);
  return (
    <AnimatedSvg className={className} title={title}>
      <circle cx="12" cy="12" r="9" />
      <path
        d="M8 12.5l2.8 2.8L16.5 9.5"
        strokeWidth={2.2}
        strokeDasharray="40"
        className={anim(t, "iconDraw")}
      />
    </AnimatedSvg>
  );
}
